"use client"

import { useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { ChevronDownSmIcon } from "@/components/ui/icons"
import { Button } from "@/components/ui/button"
import { useLocale } from "next-intl"
import {
  ComparisonGrid,
  type ComparisonGridPackage,
} from "@/components/ui/comparison-grid"
import { useCountry } from "@/components/layout/country-provider"
import { mriPackageKeysFor } from "@/data/mri-packages"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import { pricingKeys as k } from "@/i18n/pricing-keys"

/* ───────── Section header icons (raw, no circle background) ───────── */

function ImagingSectionIcon() {
  // Same figure as the MRI package tab/card icon, rendered in neutral-500.
  return <PersonPackageIcon size={20} className="shrink-0 text-neutral-500" />
}

function BloodSectionIcon() {
  // Reuse the lab-vial SVG from the membership page's "Choose your testing
  // rhythm" section (`/icons/labs.svg`).
  return (
    // eslint-disable-next-line @next/next/no-img-element -- static decorative SVG icon; next/image adds nothing for inline SVGs
    <img
      src="/icons/labs.svg"
      alt=""
      aria-hidden="true"
      className="h-7 w-auto shrink-0"
    />
  )
}

const IMAGING_COMPARISON = [
  {
    featureKey: "compHead" as const,
    core: true,
    advanced: true,
    pro: true,
    infoKey: "compHeadInfo" as const,
  },
  {
    featureKey: "compSpine" as const,
    core: true,
    advanced: true,
    pro: true,
    infoKey: "compSpineInfo" as const,
  },
  {
    featureKey: "compNeck" as const,
    core: true,
    advanced: true,
    pro: true,
    infoKey: "compNeckInfo" as const,
  },
  {
    featureKey: "compAbdomen" as const,
    core: true,
    advanced: true,
    pro: true,
    infoKey: "compAbdomenInfo" as const,
  },
  {
    featureKey: "compPelvis" as const,
    core: true,
    advanced: true,
    pro: true,
    infoKey: "compPelvisInfo" as const,
  },
  {
    featureKey: "compProstateOvaries" as const,
    core: false,
    advanced: true,
    pro: true,
    infoKey: "compProstateOvariesInfo" as const,
  },
  {
    featureKey: "compBodyComp" as const,
    core: false,
    advanced: true,
    pro: true,
    infoKey: "compBodyCompInfo" as const,
  },
  {
    featureKey: "compSpineMuscle" as const,
    core: false,
    advanced: true,
    pro: true,
    infoKey: "compSpineMuscleInfo" as const,
  },
  {
    featureKey: "compBrainAnalysis" as const,
    core: false,
    advanced: false,
    pro: true,
    infoKey: "compBrainAnalysisInfo" as const,
  },
  {
    featureKey: "compHipKnee" as const,
    core: false,
    advanced: false,
    pro: true,
    infoKey: "compHipKneeInfo" as const,
  },
]

const BLOOD_COMPARISON = [
  {
    featureKey: "compInterpretBlood" as const,
    core: true,
    advanced: true,
    pro: true,
  },
  {
    featureKey: "compAdvancedBlood" as const,
    core: false,
    advanced: true,
    pro: true,
    infoKey: "compAdvancedBloodInfo" as const,
  },
  {
    featureKey: "compHormoneTest" as const,
    core: false,
    advanced: false,
    pro: true,
    infoKey: "compHormoneTestInfo" as const,
  },
  {
    featureKey: "compBVitaminsTest" as const,
    core: false,
    advanced: false,
    pro: true,
    infoKey: "compBVitaminsTestInfo" as const,
  },
]

export function CompareMriPackagesDropdown({
  secondaryButton,
}: {
  /** Optional pill-style sibling rendered inline with the trigger
   *  (e.g. an "Insurance coverage" link). Wrap pills in a row so
   *  they breathe at the same vertical rhythm as Compare packages. */
  secondaryButton?: React.ReactNode
}) {
  const t = useTranslations("pricing")
  const [open, setOpen] = useState(false)
  const locale = useLocale()
  const { code: country } = useCountry()
  const packages = mriPackageKeysFor(
    country,
    locale
  ) as ComparisonGridPackage[]

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-8">
        <Button
          type="button"
          variant="outline"
          shape="round"
          onClick={() => setOpen(!open)}
          // On mobile (stacked), the secondary button (insurance coverage)
          // reads first; flex `order` flips Compare packages below it.
          // md+ reverts to source order so Compare sits on the left.
          className="group order-2 md:order-1"
          iconRight={
            <ChevronDownSmIcon
              className={cn(
                "transition-transform duration-200",
                open ? "rotate-180" : "group-hover:translate-y-0.5"
              )}
            />
          }
        >
          {t(k.comparePackages)}
        </Button>
        {secondaryButton}
      </div>

      {/* safe-animate-presence: conditional-mount — gated by `open=true` */}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden px-4 md:px-10"
          >
            <div className="mx-auto mb-8 max-w-[1440px] rounded-[16px] bg-white p-6">
              <ComparisonGrid
                label={t(k.comparisonImaging)}
                icon={<ImagingSectionIcon />}
                rows={IMAGING_COMPARISON.map((r) => ({
                  feature: t(r.featureKey),
                  core: r.core,
                  advanced: r.advanced,
                  pro: r.pro,
                  info: r.infoKey ? t(r.infoKey) : undefined,
                }))}
                moreInfoLabel={t(k.moreInfo)}
                packages={packages}
              />
              <div className="mt-8">
                <ComparisonGrid
                  label={t(k.comparisonBloodAnalysis)}
                  icon={<BloodSectionIcon />}
                  rows={BLOOD_COMPARISON.map((r) => ({
                    feature: t(r.featureKey),
                    core: r.core,
                    advanced: r.advanced,
                    pro: r.pro,
                    info: r.infoKey ? t(r.infoKey) : undefined,
                  }))}
                  moreInfoLabel={t(k.moreInfo)}
                  packages={packages}
                />
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  )
}
